import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  LiquityStoreState,
  Decimal,
  Trove,
  SLSD_LIQUIDATION_RESERVE,
  Percent,
  Difference,
  UserTrove,
  Fees,
} from "@liquity/lib-base";
import { useLiquitySelector } from "@liquity/lib-react";
import { TroveForm, ActionProps } from "./PSY/TroveForm";

import { useMyTransactionState } from "../Transaction";
import { useTroveView } from "./context/TroveViewContext";
import { Button } from "../PSY/Button"
import { useAppState } from "../../hooks/AppStateContext";

type Props = {
  collateral: Decimal
  setCollateral: React.Dispatch<React.SetStateAction<Decimal>>
  netDebt: Decimal
  setNetDebt: React.Dispatch<React.SetStateAction<Decimal>>
  trove: UserTrove | Trove
}

const TRANSACTION_ID = "trove-adjustment";
const APPROVAL_TRANSACTION_ID = "approve-token";
const GAS_ROOM_ETH = Decimal.from(0.1);

const feeFrom = (original: Trove, edited: Trove, borrowingRate: Decimal): Decimal => {
  const change = original.whatChanged(edited, borrowingRate);

  if (change && change.type !== "invalidCreation" && change.params.borrowSLSD) {
    return change.params.borrowSLSD.mul(borrowingRate);
  } else {
    return Decimal.ZERO;
  }
};

const applyUnsavedCollateralChanges = (unsavedChanges: Difference, trove: Trove) => {
  if (unsavedChanges.absoluteValue) {
    if (unsavedChanges.positive) {
      return trove.collateral.add(unsavedChanges.absoluteValue);
    }
    if (unsavedChanges.negative) {
      if (unsavedChanges.absoluteValue.lt(trove.collateral)) {
        return trove.collateral.sub(unsavedChanges.absoluteValue);
      }
    }
    return trove.collateral;
  }
  return trove.collateral;
};

const applyUnsavedNetDebtChanges = (unsavedChanges: Difference, trove: Trove) => {
  if (unsavedChanges.absoluteValue) {
    if (unsavedChanges.positive) {
      return trove.netDebt.add(unsavedChanges.absoluteValue);
    }
    if (unsavedChanges.negative) {
      if (unsavedChanges.absoluteValue.lt(trove.netDebt)) {
        return trove.netDebt.sub(unsavedChanges.absoluteValue);
      }
    }
    return trove.netDebt;
  }
  return trove.netDebt;
};

export const Adjusting: React.FC<Props> = ({
  collateral,
  setCollateral,
  netDebt,
  setNetDebt,
  trove,
}) => {
  const { currentCollateralKey } = useAppState();

  const selector = useCallback((state: LiquityStoreState) => {
    if (!currentCollateralKey) {
      return undefined
    }

    const { collateralStates, collateralDerivedStates } = state;
    const { balance } = collateralStates[currentCollateralKey];
    const { fees } = collateralDerivedStates[currentCollateralKey];
    return {
      fees,
      balance,
    };
  }, [currentCollateralKey]);

  const { dispatchEvent } = useTroveView();
  const selectedState = useLiquitySelector(selector);
  const previousTrove = useRef<Trove>(trove);

  const transactionState = useMyTransactionState(TRANSACTION_ID);

  useEffect(() => {
    if (transactionState.type === "confirmedOneShot") {
      dispatchEvent("TROVE_ADJUSTED");
    }
  }, [transactionState.type, dispatchEvent]);

  useEffect(() => {
    if (!previousTrove.current.collateral.eq(trove.collateral)) {
      const unsavedChanges = Difference.between(collateral, previousTrove.current.collateral);
      const nextCollateral = applyUnsavedCollateralChanges(unsavedChanges, trove);
      setCollateral(nextCollateral);
    }
    if (!previousTrove.current.netDebt.eq(trove.netDebt)) {
      const unsavedChanges = Difference.between(netDebt, previousTrove.current.netDebt);
      const nextNetDebt = applyUnsavedNetDebtChanges(unsavedChanges, trove);
      setNetDebt(nextNetDebt);
    }
    previousTrove.current = trove;
  }, [trove, collateral, netDebt]);

  if (!selectedState) {
    return <></>
  }

  const { fees, balance } = selectedState;
  const borrowingRate = fees.borrowingRate();

  const isDirty = !collateral.eq(trove.collateral) || !netDebt.eq(trove.netDebt);

  // NOTE: for fee calc
  const isDebtIncrease = netDebt.gt(trove.netDebt);
  const debtIncreaseAmount = isDebtIncrease ? netDebt.sub(trove.netDebt) : Decimal.ZERO;

  const fee = isDebtIncrease
    ? feeFrom(trove, new Trove(trove.collateral, trove.debt.add(debtIncreaseAmount)), borrowingRate)
    : Decimal.ZERO;  // calc (amout * rate)

  const totalDebt = netDebt.add(SLSD_LIQUIDATION_RESERVE).add(fee);
  const updatedTrove = isDirty ? new Trove(collateral, totalDebt) : trove;
  console.log("trove", trove.collateral.toString());
  const maxCollateral = trove.collateral.add(balance);
  console.log("🚀 ~ balance:", balance.toString());
  console.log("🚀 ~ maxCollateral:", maxCollateral.toString());
  // const collateralMaxedOut = collateral.eq(maxCollateral);   d

  return (
    <TroveForm
      trove={trove}
      updatedTrove={updatedTrove}
      maxCollateral={maxCollateral}
      borrowingRate={borrowingRate}
      collateral={collateral}
      setCollateral={setCollateral}
      netDebt={netDebt}
      setNetDebt={setNetDebt}
      transactionId={TRANSACTION_ID}
      actionLabel="Adjust"
    />
  )
};

